import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { PreviewCustomerOrderQuery } from "@saleor/graphql";
import { maybe, renderCollection } from "@saleor/misc";
import { formatDate } from "@saleor/utils/date";
import classNames from "classnames";
import React, { useMemo, useRef } from "react";
import { useIntl } from "react-intl/lib";

import useStyles from "./styles";

export interface NotMatchModalProps {
  onConfirm: () => void;
  onCancel: () => void;
  open: boolean;
  failOrderLines?: PreviewCustomerOrderQuery["previewCustomerOrder"]["failOrderLines"];
}

const NotMatchModal: React.FC<NotMatchModalProps> = ({
  open,
  onConfirm,
  onCancel,
  failOrderLines
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const dataSortByItemNo =
    useMemo(
      () =>
        failOrderLines
          ? failOrderLines.sort(
              (itemA, itemB) => Number(itemA.itemNo) - Number(itemB.itemNo)
            )
          : [],
      [failOrderLines]
    ) || [];

  return (
    <>
      <Dialog open={open} className={classes.root}>
        <DialogTitle className={classes.dialogTitle}>
          <MyTypography variant="subtitle1" className={classes.title}>
            {intl.formatMessage({
              defaultMessage: `มีประมาณการส่งมอบไม่ตรงกับวันที่ต้องการสินค้า`
            })}
          </MyTypography>
          <MyTypography variant="body2" className={classes.title}>
            &nbsp;({failOrderLines.length}&nbsp;
            {intl.formatMessage({
              defaultMessage: `รายการ)`
            })}
          </MyTypography>
        </DialogTitle>
        <DialogContent dividers={false} className={classes.dialogContent}>
          <div className={classes.rootTable} ref={containerRef}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCellHeader
                    textAlign="center"
                    className={classNames(classes.colHead, classes.col56)}
                  >
                    <MyTypography variant="subtitle3">
                      {intl.formatMessage({
                        defaultMessage: "Item No."
                      })}
                    </MyTypography>
                  </TableCellHeader>
                  <TableCellHeader
                    className={classNames(classes.colHead, classes.col140)}
                  >
                    <MyTypography variant="subtitle3">
                      {intl.formatMessage({ defaultMessage: "Material Code" })}
                    </MyTypography>
                  </TableCellHeader>
                  <TableCellHeader
                    className={classNames(classes.colHead, classes.col190)}
                  >
                    <MyTypography variant="subtitle3">
                      {intl.formatMessage({
                        defaultMessage: "Material Description"
                      })}
                    </MyTypography>
                  </TableCellHeader>
                  <TableCellHeader
                    className={classNames(classes.colHead, classes.col135)}
                  >
                    <MyTypography variant="subtitle3">
                      {intl.formatMessage({
                        defaultMessage: "วันที่ต้องการ สินค้า"
                      })}
                    </MyTypography>
                  </TableCellHeader>
                  <TableCellHeader
                    className={classNames(
                      classes.colHead,
                      classes.col156,
                      classes.estDateMatch
                    )}
                  >
                    <MyTypography variant="subtitle3">
                      {intl.formatMessage({
                        defaultMessage: "วันที่ประมาณการส่งมอบ"
                      })}
                    </MyTypography>
                  </TableCellHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {renderCollection(dataSortByItemNo, (item, index) => (
                  <TableRow key={index}>
                    <TableCell>{maybe(() => item.itemNo)}</TableCell>
                    <TableCell>
                      {maybe(() => item.materialVariantCode)}
                    </TableCell>
                    <TableCell>
                      {maybe(() => item.materialDescription)}
                    </TableCell>
                    <TableCell>
                      {maybe(() => formatDate(item.requestDeliveryDate))}
                    </TableCell>
                    <TableCell className={classes.estDateNotMatch}>
                      {maybe(() => formatDate(item.confirmedDate))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <StickyHorizontalScrollBar container={containerRef} />
          </div>
        </DialogContent>
        <DialogActions disableSpacing={true}>
          <Button
            variant="contained"
            color="secondary"
            className={classes.buttonAction}
            onClick={onConfirm}
          >
            <MyTypography variant="subtitle1">
              {intl.formatMessage({
                defaultMessage: "ยอมรับ วันส่งมอบนี้"
              })}
            </MyTypography>
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            className={classes.buttonAction}
            onClick={onCancel}
          >
            <MyTypography variant="subtitle1">
              {intl.formatMessage({
                defaultMessage: "ไม่ยอมรับ วันส่งมอบนี้"
              })}
            </MyTypography>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotMatchModal;
